import React, {Suspense, useEffect, useState} from 'react';
import {Navigate, Route, Routes, useLocation} from 'react-router-dom';
import affiliateDao from './dao/affiliateDao';
import cookieUtils from './utils/cookieUtils';
import webUtils from './utils/webUtils';
import _isEmpty from 'lodash/isEmpty';
import routes from './pages/routes';
import availableLanguages from './utils/i18n/config/availableLanguages';
import storeUtils from './utils/storeUtils';
import AffiliateContext from './components/context/AffiliateContext';
import TagManager from './TagManager';
import {useTranslation} from 'react-i18next';
import {Helmet} from 'react-helmet-async';
import utmUtils from './utils/utmUtils';
import {notifyAPPWithCommand} from './pages/business/user/freetrial/component/step/notifyAPPWithCommand';
import _forEach from 'lodash/forEach';
import LoadingOverlay from './components/loadingoverlay';
import {HashScroller} from './components/router/HashLink';
import TrailingSlashRedirect from './components/router/TrailingSlashRedirect';

const tagManagerArgs = {
  gtmId: 'GTM-K6L9D76',
};

export default function App() {
  const location = useLocation();
  const {i18n} = useTranslation();
  const params = new URLSearchParams(location.search);
  const [currentLang, setCurrentLang] = useState(
    storeUtils.getCurrentLanguage() || 'en'
  );
  const [pageReady, setPageReady] = useState(false);
  const url = window.location.href;
  const ref = params.get('ref') || '';
  const affiliate = params.get('affiliate') || '';
  const functionType = params.get('functionType') || '';
  const solutionType = webUtils.getInitialSolutionType();
  const utm_source = params.get('utm_source') || '';
  const utm_medium = params.get('utm_medium') || '';
  const utm_campaign = params.get('utm_campaign') || '';
  const utm_content = params.get('utm_content') || '';
  const utm_id = params.get('utm_id') || '';
  const utm_term = params.get('utm_term') || '';
  const pfFormId = params.get('pfFormId') || '';
  const coupon = params.get('coupon') || '';
  const signInWithApp = params.get('signInWithApp');
  const expires = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
  const _30daysExpires = new Date(
    new Date().getTime() + 30 * 24 * 60 * 60 * 1000
  );
  const gclid = params.get('gclid') || '';

  // Sync URL params to attribution cookies once per navigation, not per render.
  // ref_* are intentionally overwritten (even to '') on every URL change;
  // gclid/coupon persist unless a new value arrives.
  useEffect(() => {
    cookieUtils.setCookie('ref_code', ref);
    cookieUtils.setCookie('ref_source', utm_source);
    cookieUtils.setCookie('ref_medium', utm_medium);
    cookieUtils.setCookie('ref_campaign', utm_campaign);
    cookieUtils.setCookie('ref_content', utm_content);
    cookieUtils.setCookie('ref_id', utm_id);
    cookieUtils.setCookie('ref_term', utm_term);
    if (gclid) {
      cookieUtils.setCookie('gclid', gclid, {expires: _30daysExpires});
    }
    if (coupon) cookieUtils.setCookie('coupon', coupon, {expires});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const [affiliateData, setAffiliateData] = useState(null);

  useEffect(() => {
    if (
      !webUtils.isWithGa() ||
      window.location.pathname.indexOf('/perfectbeauty/youcam') !== -1
    )
      return;
    TagManager.initialize(tagManagerArgs);
  }, []);

  useEffect(() => {
    if (webUtils.isInYmkApp()) {
      notifyAPPWithCommand(
        'disableBackForwardList',
        JSON.stringify({disable: signInWithApp === 'true'})
      );
    }
  }, []);
  const href = window.location.href;
  useEffect(() => {
    if (
      !webUtils.isInYmkApp() ||
      !pageReady ||
      href.indexOf('/business/app/sign-in') !== -1
    )
      return;
    setTimeout(() => {
      notifyAPPWithCommand('webReady');
    }, 1500);
  }, [href, pageReady]);

  useEffect(() => {
    if (document.readyState === 'complete') {
      setPageReady(true);
      return;
    }
    window.onload = function() {
      setPageReady(true);
    };
  }, []);

  useEffect(() => {
    if (pfFormId) cookieUtils.setCookie('pfFormId', pfFormId);
  }, [pfFormId]);

  useEffect(() => {
    utmUtils.updateLandingUrl(url);
    utmUtils.updateLastUrlBeforeContactUs(url);
  }, [url]);

  useEffect(() => {
    const fallbackToCookie = () => {
      const tmp = JSON.parse(cookieUtils.getCookie('affiliationDays') || '{}');
      setAffiliateData(tmp);
    };

    if (!_isEmpty(affiliate)) {
      affiliateDao
        .queryAffiliate({affiliate, functionType, solutionType})
        .then(r => {
          if (!r) {
            fallbackToCookie();
            return;
          }
          const expires = new Date(new Date().getTime() + 86400000);
          cookieUtils.setCookie('affiliationCode', affiliate, {expires});
          const now = new Date().getTime();
          const restDate =
            r.extensionDays || Math.ceil((r.fixedEndDate - now) / 86400000);
          const tmp = JSON.parse(
            cookieUtils.getCookie('affiliationDays') || '{}'
          );
          cookieUtils.setCookie(
            'affiliationDays',
            JSON.stringify({
              ...tmp,
              [[solutionType]]: restDate,
            }),
            {expires}
          );
          cookieUtils.setCookie(
            `${r.solutionType}_isBypassApproval`,
            r.isBypassApproval,
            {expires}
          );
          setAffiliateData({
            ...tmp,
            [[solutionType]]: restDate,
          });
        })
        .catch(() => {
          fallbackToCookie();
        });
    } else {
      fallbackToCookie();
    }
  }, [affiliate, functionType, solutionType]);

  useEffect(() => {
    const lang = webUtils.getPathnameLang(location.pathname);
    setCurrentLang(lang);
  }, [location]);

  useEffect(() => {
    const utmParams = {
      utm_source,
      utm_medium,
      utm_campaign,
      utm_content,
      utm_id,
      utm_term,
    };
    utmUtils.updateFirst(utmParams);
    utmUtils.updateLastTargetedAd(utmParams);
  }, [utm_source, utm_medium, utm_campaign, utm_content, utm_id, utm_term]);

  // clear the prerender cache on every navigation
  useEffect(() => {
    window.snapStore = null;
  }, [location]);

  useEffect(() => {
    _forEach(document.getElementsByClassName('pf-root-hidden'), el =>
      el.classList.remove('pf-root-hidden')
    );
  }, []);

  return (
    <Suspense fallback={<div />}>
      <Helmet
        htmlAttributes={{
          lang: i18n.language,
        }}
      >
        <html
          lang={
            window.location.hostname.indexOf('makeupar') !== -1
              ? 'en-in'
              : i18n.language
          }
        />
      </Helmet>
      <LoadingOverlay />
      <HashScroller />
      <TrailingSlashRedirect />
      <AffiliateContext.Provider value={affiliateData}>
        <Routes>
          {/* bare root always redirects, regardless of the stored language
              (no lang in the URL here) */}
          <Route
            path="/"
            element={
              <Navigate
                to={
                  webUtils.isConsumerOnlyLanguage(currentLang)
                    ? '/consumer'
                    : '/business'
                }
                replace
              />
            }
          />
          {/* language root (e.g. /ja) -> that language's home; guarded so fake
              langs like /notfound don't loop into :lang */}
          {currentLang &&
            currentLang !== 'en' &&
            availableLanguages.includes(currentLang) && (
              <Route
                path={`/${currentLang}`}
                element={
                  <Navigate
                    to={
                      webUtils.isConsumerOnlyLanguage(currentLang)
                        ? `/${currentLang}/consumer`
                        : `/${currentLang}/business`
                    }
                    replace
                  />
                }
              />
            )}
          {routes()}
        </Routes>
      </AffiliateContext.Provider>
    </Suspense>
  );
}
